import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function AdminPrivacy() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/privacy").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-[#0B0B0C]/50">Laden...</p>;

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setSection = (i, k, v) =>
    setData((d) => ({ ...d, sections: d.sections.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const addSection = () => setData((d) => ({ ...d, sections: [...d.sections, { title: "", body: "" }] }));
  const removeSection = (i) => setData((d) => ({ ...d, sections: d.sections.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/privacy", { intro: data.intro, updated: data.updated, sections: data.sections });
      toast.success("Privacybeleid opgeslagen");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-privacy-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-4xl text-[#0A3087]">Privacybeleid</h1>
          <p className="text-[#0B0B0C]/60 text-sm mt-1">Bewerk de tekst van de privacyverklaring per sectie</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-privacy-save" className="px-5 py-2.5 rounded-md text-white text-sm font-600 disabled:opacity-60" style={{ background: "var(--elsa-blue)" }}>
          {saving ? "Opslaan..." : "Opslaan"}
        </button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-6 max-w-3xl">
        <div>
          <Label htmlFor="intro">Inleiding</Label>
          <Textarea id="intro" data-testid="admin-privacy-intro" value={data.intro} onChange={(e) => setField("intro", e.target.value)} className="mt-1.5" rows={3} />
        </div>

        <div className="space-y-5">
          {data.sections.map((s, i) => (
            <div key={i} className="rounded-lg border border-[#E2E8F0] p-4" data-testid={`admin-privacy-section-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="flex items-center gap-2 text-xs text-[#0B0B0C]/40"><GripVertical size={14} /> Sectie {i + 1}</span>
                <button onClick={() => removeSection(i)} data-testid={`admin-privacy-remove-${i}`} className="h-8 w-8 grid place-items-center rounded-md border border-[#E2E8F0] hover:border-red-500 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
              <Label htmlFor={`t-${i}`}>Titel</Label>
              <Input id={`t-${i}`} data-testid={`admin-privacy-title-${i}`} value={s.title} onChange={(e) => setSection(i, "title", e.target.value)} className="mt-1.5 mb-3" />
              <Label htmlFor={`b-${i}`}>Tekst</Label>
              <Textarea id={`b-${i}`} data-testid={`admin-privacy-body-${i}`} value={s.body} onChange={(e) => setSection(i, "body", e.target.value)} className="mt-1.5" rows={5} />
            </div>
          ))}
          <button onClick={addSection} data-testid="admin-privacy-add-section" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-[#E2E8F0] text-sm font-500 hover:border-[#0A3087] hover:text-[#0A3087]">
            <Plus size={16} /> Sectie toevoegen
          </button>
        </div>

        <div>
          <Label htmlFor="updated">Laatst bijgewerkt (datum)</Label>
          <Input id="updated" data-testid="admin-privacy-updated" value={data.updated} onChange={(e) => setField("updated", e.target.value)} className="mt-1.5" />
        </div>

        <p className="text-xs text-[#0B0B0C]/50">Tip: e-mailadressen en links (www. of https://) worden automatisch klikbaar op de pagina. De knop "Cookievoorkeuren aanpassen" staat sowieso onderaan de pagina.</p>
      </div>
    </div>
  );
}
